import {
  useCreateCustomLevelMutation,
  useUpdateCustomLevelMutation,
} from '@/app/redux/endpoints/customLevels';
import { useUpdateLessonPlanMutation } from '@/app/redux/endpoints/lessonPlans';
import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useAppSelector } from '@/app/redux/hooks';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useLessonPlans } from '@/app/redux/hooks/lessonPlans';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  CustomLevel,
  UpdateCustomLevelInput,
} from '@/common/types/customLevel';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { useAutosave } from '@/common/utils/useAutosave';
import CustomLevelEditor, {
  GameId,
} from '@/dashboard/CustomLevel/CustomLevelEditor';
import { XIcon } from '@phosphor-icons/react';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { generateCustomLevelFromContent } from './generateCustomLevelFromContent';

interface Props {
  lessonPlanId: string | undefined;
  content: string;
  lessonPlanName: string;
  outOfSync: boolean;
  onDismissOutOfSync: () => void;
  setIsSaving: (isSaving: boolean) => void;
  setLastSaved: (lastSaved: number | undefined) => void;
}

export default function LessonPlanGameLevel({
  lessonPlanId,
  content,
  lessonPlanName,
  outOfSync,
  onDismissOutOfSync,
  setIsSaving,
  setLastSaved,
}: Props) {
  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);
  const { lessonPlansById } = useLessonPlans();
  const { customLevels } = useCustomLevels();
  const { students } = useStudents();
  const [createCustomLevel] = useCreateCustomLevelMutation();
  const [updateCustomLevel] = useUpdateCustomLevelMutation();
  const [updateLessonPlan] = useUpdateLessonPlanMutation();
  const [updateStudent] = useUpdateStudentMutation();

  const [isReady, setIsReady] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(false);
  const hasTriggeredRef = useRef(false);
  const [localLevel, setLocalLevel] = useState<CustomLevel | null>(null);

  const lessonPlan = lessonPlanId
    ? lessonPlansById.get(lessonPlanId)
    : undefined;
  const customLevelId = lessonPlan?.customLevelId;
  const serverLevel = customLevelId
    ? customLevels.find((l) => l.id === customLevelId)
    : undefined;

  const { isSaving } = useAutosave({
    saveFn: handleSave,
    trigger: [localLevel],
    disabled: !isReady,
  });

  useEffect(() => {
    if (serverLevel && !localLevel) {
      setLocalLevel(serverLevel);
      setIsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLevel, localLevel]);

  useEffect(() => {
    setIsSaving(isSaving);
    return () => setIsSaving(false);
  }, [isSaving, setIsSaving]);

  useEffect(() => {
    if (
      !lessonPlanId ||
      customLevelId ||
      !userId ||
      !currentOrganisationId ||
      hasTriggeredRef.current
    ) {
      return;
    }

    hasTriggeredRef.current = true;
    setIsGenerating(true);
    setGenerateError(false);

    const generated = generateCustomLevelFromContent(content, lessonPlanName);

    createCustomLevel({
      ...generated,
      createdBy: userId,
      organisationId: currentOrganisationId,
    })
      .unwrap()
      .then((newLevel) =>
        updateLessonPlan({
          orgId: currentOrganisationId,
          id: lessonPlanId,
          updates: { customLevelId: newLevel.id },
        }).unwrap(),
      )
      .catch((err) => {
        console.error('Failed to generate game level:', err);
        toast.error('Failed to generate game level', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        setGenerateError(true);
        hasTriggeredRef.current = false;
      })
      .finally(() => {
        setIsGenerating(false);
      });
  }, [lessonPlanId, customLevelId, userId, currentOrganisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRegenerate() {
    if (!customLevelId || !currentOrganisationId) return;

    setIsGenerating(true);
    try {
      const generated = generateCustomLevelFromContent(content, lessonPlanName);
      const {
        description,
        mysticMatch,
        wordWeaver,
        wordSums,
        phonemeForge,
        sentenceBuilder,
      } = generated;
      await updateCustomLevel({
        orgId: currentOrganisationId,
        id: customLevelId,
        updates: {
          description,
          mysticMatch,
          wordWeaver,
          wordSums,
          phonemeForge,
          sentenceBuilder,
        },
      }).unwrap();
      toast.success('Game level regenerated');
    } catch (err) {
      console.error('Failed to regenerate game level:', err);
      toast.error('Failed to regenerate game level', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSave() {
    if (!localLevel || !currentOrganisationId) return;
    const {
      id: _id,
      createdBy: _cb,
      organisationId: _oi,
      createdAt: _ca,
      updatedAt: _ua,
      ...updates
    } = localLevel;
    await updateCustomLevel({
      orgId: currentOrganisationId,
      id: localLevel.id,
      updates,
    }).unwrap();
    setLastSaved(Date.now());
  }

  function handleChange(updates: UpdateCustomLevelInput) {
    setLocalLevel((prev) => (prev ? { ...prev, ...updates } : null));
  }

  async function handleSelectStudent(studentId: string) {
    if (!currentOrganisationId || !localLevel?.id) return;
    const student = students.find((s) => s.id === studentId);
    if (!student) return;

    const isAssigned = student.customLevelId === localLevel.id;
    trackMixpanelEvent('Custom Level Learner Toggled', {
      customLevelId: localLevel.id,
      studentId,
      assigned: !isAssigned,
    });
    const newCustomLevelId = isAssigned ? null : localLevel.id;
    const currentStudents = localLevel.students ?? {};
    let updatedStudents: CustomLevel['students'];
    if (isAssigned) {
      updatedStudents = { ...currentStudents };
      delete updatedStudents[studentId];
    } else {
      updatedStudents = {
        ...currentStudents,
        [studentId]: { completedPlaythroughs: 0, gamesProgress: {} },
      };
    }
    try {
      await Promise.all([
        updateStudent({
          orgId: currentOrganisationId,
          studentId,
          updates: { customLevelId: newCustomLevelId },
        }).unwrap(),
        handleChange({ students: updatedStudents }),
      ]);
    } catch {
      toast.error('Failed to update learner assignment');
    }
  }

  function handleToggleGame(game: GameId, enabled: boolean) {
    if (!localLevel) return;
    trackMixpanelEvent('Custom Level Game Toggled', {
      customLevelId: localLevel.id,
      game,
      enabled,
    });
    handleChange({ [game]: { ...localLevel[game], enabled } });
  }

  if (!lessonPlanId) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground p-8">
        Save the lesson plan first to generate a game level.
      </div>
    );
  }

  if (isGenerating || (!customLevelId && !generateError)) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (generateError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
        <p className="text-sm text-muted-foreground">
          Failed to generate game level.
        </p>
        <button
          className="text-sm underline text-foreground"
          onClick={() => {
            hasTriggeredRef.current = false;
            setGenerateError(false);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!localLevel) return null;

  const assignedStudentIds = students
    .filter((s) => s.customLevelId === localLevel.id)
    .map((s) => s.id);

  return (
    <div className="flex flex-col h-full">
      {outOfSync && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800">
          <span>
            The lesson plan has changed since the game level was last generated.
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={async () => {
                    await handleRegenerate();
                    onDismissOutOfSync();
                  }}
                  className="font-medium underline underline-offset-2 hover:text-amber-900"
                >
                  Regenerate game level
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <strong>Note:</strong> This will overwrite any changes you've
                made to the game level
              </TooltipContent>
            </Tooltip>
            <button
              onClick={onDismissOutOfSync}
              className="hover:text-amber-900"
              aria-label="Dismiss"
            >
              <XIcon />
            </button>
          </div>
        </div>
      )}
      <CustomLevelEditor
        level={localLevel}
        onChange={handleChange}
        assignedStudentIds={assignedStudentIds}
        onSelectStudent={handleSelectStudent}
        onToggleGame={handleToggleGame}
      />
    </div>
  );
}
